import { defu } from 'defu'
import { state, getFaro } from '../state'
import { track } from './track'
import { getIdentityStore } from '../identity-store'
import { _notifyContactInfoChange } from './has-contact-info'

// Suggest common identify fields while keeping traits flexible
type IdentifyTraits = Record<string, string> & {
  userId?: string;
  email?: string;
  phone?: string;
};

// Types for the possible arguments of the identify function
type IdentifyArgs =
  | [IdentifyTraits]
  | [string, IdentifyTraits?]
  | [string];

interface ParsedIdentifyArgs {
  userId?: string;
  email?: string;
  phone?: string;
  traits: IdentifyTraits;
}

/**
 * Identifies a user with traits.
 * @param traits User traits
 */
export function identify(traits: IdentifyTraits): Promise<void>;
export function identify(userId: string, traits?: IdentifyTraits): Promise<void>;
export async function identify(...args: IdentifyArgs): Promise<void> {
  const { userId, email, phone, traits } = parseIdentifyArgs(...args);

  // Persist identity
  const identityStore = getIdentityStore()
  identityStore.setIdentity(userId, { ...traits, email, phone })

  // Update context (legacy snapshot — Faro is the truth)
  if (state.context) {
    if (userId) state.context.user.id = userId
    if (email) state.context.user.email = email
    if (phone) state.context.user.phone = phone
    state.context.user.traits = { ...state.context.user.traits, ...traits }
  }

  // Write to Faro — canonical metadata store. Merges with previous setUser
  // so partial identify() calls are non-destructive.
  writeFaroUser({ userId, email, phone, traits })

  // Notify hasContactInfo subscribers — fires only when the boolean flips
  // (dedup is internal). Composables like Nuxt's useContactInfo react to this.
  _notifyContactInfoChange()

  // Single dispatch: track('identify'). Destinations consume user data via
  // the mapping processor (faro.metas.value.user.*) — no separate identify
  // path on the bus.
  return track('identify')
}

/** camelCase → snake_case (`emailOptin` → `email_optin`). */
function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, m => `_${m.toLowerCase()}`)
}

/** Infer language from navigator (`pt-BR` → `pt_BR`). */
function inferLanguage(): string | undefined {
  if (typeof navigator === 'undefined') return undefined
  const lang = navigator.language
  if (!lang) return undefined
  return lang.replace('-', '_')
}

/**
 * Stringify + snake_case-normalize trait keys for Faro's `MetaUser.attributes`.
 * This is the single canonical normalization point — destinations consume the
 * normalized shape via `faro.metas.value.user.attributes.<snake_case>` through
 * their mapping JSON.
 */
function stringifyTraits(traits: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(traits)) {
    if (v === undefined || v === null) continue
    const key = camelToSnake(k)
    if (typeof v === 'string') out[key] = v
    else if (typeof v === 'number' || typeof v === 'boolean') out[key] = String(v)
  }
  // Auto-inject browser language when caller didn't provide one.
  if (!out.language) {
    const inferred = inferLanguage()
    if (inferred) out.language = inferred
  }
  return out
}

/**
 * Push user identity to Faro. Merges with the existing `user` meta so partial
 * identify() calls (e.g., adding a trait later) don't clobber prior values.
 *
 * Exported so `api/load.ts` can populate Faro from persisted identity at
 * boot — without going through the full `track('identify')` path. The
 * Insider destination later reads `faro.metas.value.user.*` via the
 * mapping processor (id 10) when synthesizing its user payload.
 */
export function writeFaroUser(payload: {
  userId?: string
  email?: string
  phone?: string
  traits: Record<string, string>
}): void {
  const faro = getFaro()
  if (!faro) return

  const next: Record<string, unknown> = {
    attributes: stringifyTraits(payload.traits),
  }
  if (payload.userId) next.id = payload.userId
  if (payload.email) next.email = payload.email
  if (payload.phone) (next.attributes as Record<string, string>).phone = payload.phone

  faro.api.setUser(defu(next, faro.metas.value.user ?? {}))
}

function parseIdentifyArgs(...args: IdentifyArgs): ParsedIdentifyArgs {
  if (typeof args[0] === 'string') {
    const traits = (args[1] as IdentifyTraits) ?? {}
    const { email, phone, userId } = traits;
    return {
      userId: args[0] ?? userId,
      email,
      phone,
      traits,
    };
  }
  const traits = args[0] ?? {} as IdentifyTraits;

  const { userId, email, phone } = traits;
  return {
    userId,
    email,
    phone,
    traits,
  };
}
